import { motion } from "framer-motion";
import { MapPin, Navigation, Clock, ArrowUpRight } from "lucide-react";
import ChapterHeading from "./ChapterHeading";

const IMG = "/photos/lago-castel-gandolfo.jpg";
const VILLA_IMG = "/photos/villa-grazioli.jpg";

const schedule = [
  { time: "17:30", title: "Accoglienza degli ospiti nel giardino principale" },
  { time: "18:00", title: "Cerimonia presso la Galleria del Pannini" },
  { time: "18:45", title: "Aperitivo nelle sale affrescate" },
  { time: "20:00", title: "Cena presso la Terrazza Grazioli" },
  { time: "23:00", title: "Taglio della torta e festeggiamenti" },
];

export default function Venue() {
  return (
    <section id="luogo" className="px-6 sm:px-12 lg:px-24 py-20 sm:py-28 bg-cream/60 border-y border-sand/60" data-testid="venue-section">
      <div className="mx-auto max-w-7xl">
        <ChapterHeading
          number="02"
          title="Il Luogo & Il Programma"
          testid="venue-heading"
        />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="md:col-span-5 flex flex-col gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl border border-sand bg-parchment overflow-hidden flex flex-col"
              data-testid="venue-map-card"
            >
              <div className="h-44 sm:h-52 overflow-hidden group">
                <img
                  src={VILLA_IMG}
                  alt="Villa Grazioli di Grottaferrata"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  data-testid="venue-villa-image"
                />
              </div>
              <div className="relative p-8 sm:p-10 flex-1 flex flex-col justify-between">
                <svg viewBox="0 0 400 240" className="absolute inset-0 w-full h-full opacity-70" fill="none" aria-hidden="true">
                  <path d="M 30 200 C 120 80, 220 220, 370 60" className="flight-path" stroke="#6E7E65" strokeWidth="1.5" />
                </svg>
                <div className="relative">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                    Coordinate di Decollo
                  </span>
                  <div className="mt-6 flex items-start gap-3">
                    <MapPin className="w-6 h-6 text-terracotta shrink-0 mt-1 soft-float" />
                    <div>
                      <h3 className="font-serif text-2xl text-[#2D2A26]">Villa Grazioli</h3>
                      <p className="mt-2 text-sm text-[#5C564E] leading-relaxed">
                        Via Umberto Pavoni 19<br />
                        Grottaferrata (RM), Castelli Romani
                      </p>
                    </div>
                  </div>
                </div>
                <div className="relative mt-8">
                  <p className="text-sm text-[#5C564E] mb-5 leading-relaxed">
                    Parcheggio disponibile in loco.
                  </p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Villa+Grazioli+Grottaferrata"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 rounded-full border border-charcoal/20 bg-panna px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#2D2A26] transition-all duration-300 hover:bg-charcoal hover:text-panna"
                    data-testid="map-open-button"
                  >
                    <Navigation className="w-4 h-4" />
                    Apri in Google Maps
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl border border-sand bg-panna p-8 sm:p-10"
              data-testid="venue-about-card"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
                La Villa
              </span>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#5C564E]">
                Dimora cinquecentesca immersa in un parco secolare dei Castelli
                Romani, Villa Grazioli custodisce la Galleria affrescata dal
                Pannini, sale storiche ed una cornice paesaggistica d'eccezione.
              </p>
              <a
                href="https://www.villa-grazioli.it/la-villa/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-terracotta hover:text-charcoal transition-colors duration-300"
                data-testid="villa-website-link"
              >
                Scopri la villa
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7 rounded-2xl border border-sand bg-panna overflow-hidden"
            data-testid="venue-program-card"
          >
            <div className="relative h-56 sm:h-64 overflow-hidden group">
              <img
                src={IMG}
                alt="Lago di Castel Gandolfo"
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                data-testid="venue-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
              <div className="absolute bottom-4 left-6 font-mono text-[10px] uppercase tracking-[0.25em] text-panna/90 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Programma della giornata
              </div>
            </div>
            <ul className="divide-y divide-sand/80">
              {schedule.map((s, i) => (
                <motion.li
                  key={s.time}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="flex items-center gap-6 px-8 sm:px-10 py-5"
                  data-testid={`schedule-item-${i}`}
                >
                  <span className="font-mono text-sm text-terracotta w-12 shrink-0">{s.time}</span>
                  <span className="h-px w-6 bg-sand shrink-0" />
                  <span className="text-sm sm:text-base text-[#2D2A26]">{s.title}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
